import { Component, ElementRef, EventEmitter, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { CheckIsCodeExistsRequest, ImageEnum, ProductClient, ProductEnum, ProductRequest } from 'src/app/shared/api-connector/sso-connector';
import { AlertService } from 'src/app/shared/services/alert.service';
import { UploadService } from 'src/app/shared/services/upload.service';
import { AutocompleteCacheService } from 'src/app/shared/services/autocomplete-cache.service';
import { AddBodyUsedDialogComponent } from '../../dialog/add-body-used-dialog/add-body-used-dialog.component';
import { AddProductUnitDialogComponent } from '../../dialog/add-product-unit-dialog/add-product-unit-dialog.component';
import { AddManufacturerDialogComponent } from '../../dialog/add-manufacturer-dialog/add-manufacturer-dialog.component';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { AddPositionDialogComponent } from '../../dialog/add-position-dialog/add-position-dialog.component';
import { AddCategoryDialogComponent } from '../../dialog/add-category-dialog/add-category-dialog.component';
import { DrawerRef } from 'src/app/shared/components/drawer/drawer-ref';

@Component({
    selector: 'app-add-hang-hoa-drawer',
    templateUrl: './add-hang-hoa-drawer.component.html',
    styleUrls: ['./add-hang-hoa-drawer.component.scss']
})
export class AddHangHoaDrawerComponent implements OnInit {
    // Injected by DrawerService
    drawerRef: DrawerRef<AddHangHoaDrawerComponent>;

    myFormAddHangHoa = new FormGroup({
        codeCtrl: new FormControl('', {
            updateOn: 'change',
            asyncValidators: [this.validateCodeNotTaken.bind(this)],
        }),
        barcodeCtrl: new FormControl('', {
            updateOn: 'change',
            asyncValidators: [this.validateBarCodeNotTaken.bind(this)],
        }),
        titleCtrl: new FormControl('', {
            validators: [Validators.required],
            updateOn: 'change',
            asyncValidators: [this.validateTitleNotTaken.bind(this)],
        }),
        abbreviationsCtrl: new FormControl('', {
            updateOn: 'change',
            asyncValidators: [this.validateAbbreviationsNotTaken.bind(this)],
        }),
        categoryCtrl: new FormControl('', [Validators.required]),
        positionCtrl: new FormControl(''),
        priceCtrl: new FormControl(0, [
            Validators.required,
            Validators.min(1),
        ]),
        priceCostCtrl: new FormControl(0, [
            Validators.required,
            Validators.min(1),
        ]),
        weightCtrl: new FormControl(0),
        isHomepageCtrl: new FormControl(true),
        inventoryMaxCtrl: new FormControl(999999999),
        inventoryMinCtrl: new FormControl(0),
        descriptionCtrl: new FormControl(''),
        noteOrderCtrl: new FormControl(''),
        manufacturerCtrl: new FormControl(''),
        quocGiaCtrl: new FormControl(''),
        productUnitCtrl: new FormControl(''),
        hangHoaThanhPhanCtrl: new FormControl(''),
        inventoryCtrl: new FormControl(0),
    });
    ctrls = this.myFormAddHangHoa.controls;

    isBatchExpireControl = false;
    reloadCategoryComponent = new EventEmitter();
    reloadPositionComponent = new EventEmitter();
    reloadProductUnitComponent = new EventEmitter();
    reloadManufacturerComponent = new EventEmitter();
    scrollStrategy: ScrollStrategy;
    lstImage: string[] = [];
    totalFile = 0;
    isLoadingAvatar: boolean;

    constructor(
        private spinner: NgxSpinnerService,
        private alertService: AlertService,
        private productClient: ProductClient,
        private uploadService: UploadService,
        private dialog: MatDialog,
        private readonly scrollStrategyOptions: ScrollStrategyOptions,
        private el: ElementRef,
        private autocompleteCache: AutocompleteCacheService,
    ) { }

    ngOnInit(): void {
        this.scrollStrategy = this.scrollStrategyOptions.noop();
    }

    close() {
        this.drawerRef.close();
    }

    handleChange() {
        if (this.isBatchExpireControl) {
            this.ctrls.hangHoaThanhPhanCtrl.setValue('');
        }
    }

    onfocusInvalid() {
        for (const key of Object.keys(this.myFormAddHangHoa.controls)) {
            if (this.myFormAddHangHoa.controls[key].invalid) {
                const invalidControl = this.el.nativeElement.querySelector('[formcontrolname="' + key + '"]');
                if (invalidControl) {
                    invalidControl.focus();
                }
                break;
            }
        }
    }

    onSubmit() {
        this.myFormAddHangHoa.markAllAsTouched();
        this.onfocusInvalid();
        if (this.myFormAddHangHoa.valid) {
            this.spinner.show();
            this.productClient.createOrUpdate(new ProductRequest({
                abbreviations: this.ctrls.abbreviationsCtrl.value,
                barcode: this.ctrls.barcodeCtrl.value,
                bodyUsedId: null,
                categoryId: this.ctrls.categoryCtrl.value ? this.ctrls.categoryCtrl.value.id : null,
                code: this.ctrls.codeCtrl.value,
                description: this.ctrls.descriptionCtrl.value,
                inventoryMax: this.ctrls.inventoryMaxCtrl.value,
                inventoryMin: this.ctrls.inventoryMinCtrl.value,
                isHomepage: this.ctrls.isHomepageCtrl.value,
                manufacturerId: this.ctrls.manufacturerCtrl.value ? this.ctrls.manufacturerCtrl.value.id : null,
                noteOrder: this.ctrls.noteOrderCtrl.value,
                positionId: this.ctrls.positionCtrl.value ? this.ctrls.positionCtrl.value.id : null,
                quocGiaId: this.ctrls.quocGiaCtrl.value ? this.ctrls.quocGiaCtrl.value.id : null,
                registrationNumber: null,
                title: this.ctrls.titleCtrl.value?.title || this.ctrls.titleCtrl.value,
                weight: this.ctrls.weightCtrl.value,
                type: ProductEnum.HangHoa,
                price: this.ctrls.priceCtrl.value,
                priceCost: this.ctrls.priceCostCtrl.value,
                lstImage: this.lstImage,
                productUnitId: this.ctrls.productUnitCtrl.value ? this.ctrls.productUnitCtrl.value.id : null,
                inventory: this.ctrls.inventoryCtrl.value
            }))
                .pipe(finalize(() => this.spinner.hide()))
                .subscribe(x => {
                    this.alertService.success('Thêm mới hàng hóa thành công', 'Thành công');
                    this.autocompleteCache.clearType('product_');
                    this.drawerRef.close(true);
                });
        }
    }

    getUrlFile(id: string) {
        return this.uploadService.getUrlFile(id);
    }

    removeImage(imageId) {
        this.lstImage = this.lstImage.filter(x => x != imageId);
        this.totalFile = this.lstImage.length;
    }

    onMultipeFileChange(event) {
        var filesAmount = event.target.files.length;
        if (filesAmount > 5) {
            this.alertService.error("Không vượt quá 5 file", "Lỗi upload file");
            return;
        }
        this.totalFile = filesAmount;
        this.lstImage = [];
        if (filesAmount === 0) {
            return;
        }
        if (event.target.files && event.target.files[0]) {
            this.uploadService
                .uploadMultipeFile(event.target.files, 0, ImageEnum.None)
                .pipe(finalize(() => (this.isLoadingAvatar = false)))
                .subscribe((x: any) => {
                    x.forEach((element) => {
                        this.lstImage.push(element.Id);
                    });
                });
        }
    }

    validateCodeNotTaken(control: FormControl): any {
        if (control.value) {
            return this.productClient.checkIsCodeExists(new CheckIsCodeExistsRequest({
                code: control.value,
            })).pipe(
                map(res => {
                    return res ? { codeTaken: true } : null;
                }));
        } else {
            return of(null);
        }
    }

    validateBarCodeNotTaken(control: FormControl): any {
        if (control.value) {
            return this.productClient.checkIsBarCodeExists(new CheckIsCodeExistsRequest({
                code: control.value,
            })).pipe(
                map(res => {
                    return res ? { barcodeTaken: true } : null;
                }));
        } else {
            return of(null);
        }
    }

    validateTitleNotTaken(control: FormControl): any {
        if (control.value) {
            return this.productClient.checkIsTitleExists(new CheckIsCodeExistsRequest({
                code: control.value?.title || control.value,
            })).pipe(
                map(res => {
                    return res ? { titleTaken: true } : null;
                }));
        } else {
            return of(null);
        }
    }

    validateAbbreviationsNotTaken(control: FormControl): any {
        if (control.value) {
            return this.productClient.checkIsAbbreviationsExists(new CheckIsCodeExistsRequest({
                code: control.value,
            })).pipe(
                map(res => {
                    return res ? { abbreviationsTaken: true } : null;
                }));
        } else {
            return of(null);
        }
    }

    addManufacturer() {
        const dialogRef = this.dialog.open(AddManufacturerDialogComponent, {
            panelClass: 'custom-dialog',
            minWidth: '500px',
            disableClose: true,
            scrollStrategy: this.scrollStrategy
        });
        dialogRef.afterClosed().subscribe(x => {
            if (x) { this.reloadManufacturerComponent = x; }
        });
    }

    addProductUnit() {
        const dialogRef = this.dialog.open(AddProductUnitDialogComponent, {
            panelClass: 'custom-dialog',
            minWidth: '500px',
            disableClose: true,
            scrollStrategy: this.scrollStrategy
        });
        dialogRef.afterClosed().subscribe(x => {
            if (x) { this.reloadProductUnitComponent = x; }
        });
    }

    addPosition() {
        const dialogRef = this.dialog.open(AddPositionDialogComponent, {
            panelClass: 'custom-dialog',
            minWidth: '500px',
            disableClose: true,
            scrollStrategy: this.scrollStrategy
        });
        dialogRef.afterClosed().subscribe(x => {
            if (x) { this.reloadPositionComponent = x; }
        });
    }

    addCategoryDialog() {
        const dialogRef = this.dialog.open(AddCategoryDialogComponent, {
            panelClass: 'custom-dialog',
            minWidth: '500px',
            disableClose: true,
            scrollStrategy: this.scrollStrategy
        });
        dialogRef.afterClosed().subscribe(x => {
            if (x) { this.reloadCategoryComponent = x; }
        });
    }
}
